import React from 'react';
import { format, parseISO } from 'date-fns';

export const SMS_BASE = '/sms/campaigns';

export const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';

export const AUDIENCE_LABELS = {
  cohorts: 'Cohorts',
  alumni: 'Alumni',
  certified: 'Certified graduates',
  custom: 'Custom list',
};

export const apiError = (error, fallback) =>
  error?.response?.data?.error || fallback;

export const formatDateTime = (value) =>
  value ? format(parseISO(value), 'd MMM yyyy, HH:mm') : '–';

export const isActive = (status) => status === 'queued' || status === 'sending';

const CAMPAIGN_STATUS = {
  queued: 'bg-gray-100 text-gray-700',
  sending: 'bg-[#E6F5F5] text-persian-600',
  completed: 'bg-green-50 text-green-700',
};

const MESSAGE_STATUS = {
  pending: 'bg-gray-100 text-gray-600',
  sending: 'bg-gray-100 text-gray-600',
  sent: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  unknown: 'bg-amber-50 text-amber-700',
};

export function StatusChip({ status, kind = 'campaign' }) {
  const styles = kind === 'message' ? MESSAGE_STATUS : CAMPAIGN_STATUS;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] capitalize ${
        styles[status] || 'bg-gray-100 text-gray-600'
      }`}
    >
      {kind === 'campaign' && isActive(status) && (
        <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
      )}
      {status}
    </span>
  );
}

// One-line summary of a stored audience object.
export const describeAudience = (audience) => {
  if (!audience) return '';
  const label = AUDIENCE_LABELS[audience.type] ?? audience.type;
  const parts = [];
  if (audience.cohort_ids?.length) {
    parts.push(
      `${audience.cohort_ids.length} cohort${
        audience.cohort_ids.length === 1 ? '' : 's'
      }`
    );
  }
  if (audience.years?.length) parts.push(`ended ${audience.years.join(', ')}`);
  if (audience.phone_numbers?.length) {
    parts.push(`${audience.phone_numbers.length.toLocaleString()} numbers`);
  }
  if (audience.student_ids?.length) {
    parts.push(`${audience.student_ids.length.toLocaleString()} students`);
  }
  if (!parts.length && audience.type !== 'custom') parts.push('all');
  return `${label}${parts.length ? ` · ${parts.join(' · ')}` : ''}`;
};

export const UNKNOWN_EXPLANATION =
  "Unknown: Advanta didn't confirm these. They may have been delivered, so they are not resent automatically.";

// Shown on 403 (no permission) and 404 (SMS service not deployed yet).
export function SmsUnavailable({ status, onRetry }) {
  let message = "We couldn't load SMS campaigns.";
  if (status === 403) message = 'Ask a SuperAdmin for SMS access.';
  if (status === 404)
    message = "SMS campaigns aren't available on the server yet.";
  return (
    <div className={`${CARD} p-8 text-center space-y-3`}>
      <p className="text-sm text-gray-600">{message}</p>
      {onRetry && status !== 403 && status !== 404 && (
        <button
          type="button"
          onClick={onRetry}
          className="bg-claret-500 hover:bg-claret-400 px-4 py-1.5 rounded-md text-white text-sm"
        >
          Retry
        </button>
      )}
    </div>
  );
}
